import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  Divider,
  Alert,
  ToggleButton,
  Tooltip,
  Checkbox,
  FormControlLabel,
  TextField,
  FormHelperText,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  Radio,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import DateInput from "../common/DateInput";
import WarningIcon from "@mui/icons-material/Warning";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  lineChartConfigSelector,
  lineChartConfigDispatcher,
  getCategoryRollupScope,
  StatTestType,
  SignificanceLevel,
  StatMarkerStyle,
  StatTestingTimeStrategy,
  YAxisRangeMode,
} from "@store/slices/line-chart-config-slice";
import {
  chartDrawerSelector,
  chartDrawerDispatcher,
} from "@store/slices/chart-drawer-slice";
import { dataAvailabilityPreviewSelector } from "@store/slices/data-availability-preview-slice";
import { getEffectiveAvailabilityDate } from "@utils/data-availability-preview-utils";
import { questionSessionMemory } from "@/utils/question-session-memory";
import ConfigurableLineChart from "./ConfigurableLineChart";
import HPButton from "../common/Button";
import HPInput from "../common/Input";
import IOSSwitch from "../common/Switch";
import DrawerFilterToggles from "./common/DrawerFilterToggles";
import TimeSeriesViewerOptionsSection from "./TimeSeriesViewerOptionsSection";
import LoadingSpinner from "../common/LoadingSpinner";
import EmptyChartState from "../common/EmptyChartState";
import { useLineChartConfigDrawer } from "@hooks/use-line-chart-config-drawer";
import { CHART_COLORS } from "@utils/chart-utils";
import {
  useGetDatasetsQuery,
  useGetDatasetByIdQuery,
  useGetWeightConfigurationsQuery,
  useGetDatasetQuestionVariablesByElementQuery,
} from "@/store/api/dataset-api";
import LineChartCustomizationPanel from "./LineChartCustomizationPanel";
import LineChartRollingAverageSection from "./LineChartRollingAverageSection";
import LineChartBreakoutSection from "./LineChartBreakoutSection";
import PerLineWeightConfig from "./PerLineWeightConfig";
import FocusModeSection from "./bar-chart-config/components/FocusModeSection";
import CategoryRollupsSection from "@components/charts/CategoryRollupsSection";
import CategoryRollupsNotice from "@components/charts/CategoryRollupsNotice";
import ApplyColorToAll from "@components/charts/ApplyColorToAll";
import type { WidgetRollup } from "@models/Rollup";
import {
  getRollingAverageDescriptionForLineChart,
  getRollingAverageNoteForLineChart,
  getRollingAverageTitleSuffix,
  getMissingDataWarningForLineChart,
  countPeriodsInRange,
} from "@utils/rolling-average-utils";
import {
  resolveLatestAvailableEndDate,
  resolveLatestAvailableSummary,
} from "@utils/data-availability-utils";
import {
  buildDefaultDateGuardrails,
  type TimeSeriesUnit,
  type UnitGuardrail,
  type GuardrailRollingOption,
} from "@models/Dataset";
import { trimDateRangeToCompletePeriods } from "@utils/time-period-utils";
import { useLineChartVisualizationBase } from "@hooks/use-line-chart-visualization-base";
import type { VisualizationBase } from "@models/VisualizationBase";
import { isOnlyBaseNameChanged } from "@utils/visualization-base-utils";
import BaseSection from "@components/charts/common/base-config/BaseSection";
import { useLineMetricSourceRevalidation } from "@hooks/use-line-metric-source-revalidation";
import MetricSourceSummarySection from "@components/charts/bar-chart-config/components/MetricSourceSummarySection";

const ALLOWED_ROLLUP_NAME_CHARS = /^[a-zA-Z0-9 _\-]*$/;

const LineChartConfigDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentConfig, datasetName, activeFilters, datasetId, questionId } =
    useAppSelector(lineChartConfigSelector);
  const { isDrawerOpen, editingWidgetId, pendingMemoryEntry, dashboardId } =
    useAppSelector(chartDrawerSelector);
  const dataAvailabilityPreview = useAppSelector(dataAvailabilityPreviewSelector);
  const questions = useAppSelector((state) => state.lineChartConfig.question);
  const [weightsApplied, setWeightsApplied] = useState<boolean>(true);
  const [dateError, setDateError] = useState<string>("");
  const [yAxisRangeError, setYAxisRangeError] = useState<string>("");
  // Stores the color each line had *before* the user started editing it.
  // Used to revert on Cancel. Cleared on Apply or palette switch.
  const [originalColors, setOriginalColors] = useState<Record<string, string>>(
    {},
  );

  // Toggle for using dashboard date filters in preview.
  // Auto-enable when df_start/df_end are present in the URL so the preview
  // matches Bar/Table/Crosstab drawers and reflects
  const [usePreviewDateFilter, setUsePreviewDateFilter] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return Boolean(params.get("df_start") && params.get("df_end"));
  });

  const { data: datasets = [] } = useGetDatasetsQuery({});
  const { data: weightConfigData } = useGetWeightConfigurationsQuery(
    datasetId as string,
    { skip: !datasetId },
  );
  const { data: datasetDetails, isSuccess: isDatasetLoaded } =
    useGetDatasetByIdQuery(datasetId as string, { skip: !datasetId });
  // Only read dataAvailabilityDate after the query has fully resolved —
  // while loading, datasetDetails is undefined which would look the same as "cleared".
  // Substituted with the active preview date when previewing this dashboard.
  const dataAvailabilityDate = isDatasetLoaded
    ? getEffectiveAvailabilityDate(
        datasetDetails?.dataAvailabilityDate ?? null,
        dataAvailabilityPreview,
        dashboardId,
        datasetId,
      )
    : undefined;

  // True when dataset has loaded AND Available Through is explicitly null/empty
  // (chart was saved in latest_available mode but the date was later cleared)
  const isAvailabilityCleared =
    currentConfig.endDateMode === "latest_available" &&
    isDatasetLoaded &&
    !dataAvailabilityDate;

  const tsGuardrails = React.useMemo(() => {
    return (
      datasetDetails?.dateGuardrails?.timeSeries ??
      buildDefaultDateGuardrails().timeSeries
    );
  }, [datasetDetails?.dateGuardrails]);

  const activeUnitGuardrail: UnitGuardrail =
    tsGuardrails.units[currentConfig.timeUnit as TimeSeriesUnit];

  const guardrailAllowedWindowSizes: number[] =
    activeUnitGuardrail.allowedRollingAverages.filter((n) => n !== 0);

  const guardrailNoRollingDisabled: boolean =
    !activeUnitGuardrail.allowedRollingAverages.includes(
      0 as GuardrailRollingOption,
    );

  // When the dataset only allows "No Rolling Average" for the current time-series unit,
  // the "Apply Rolling Average" checkbox should be disabled & forced off.
  const guardrailRollingDisabled: boolean =
    guardrailAllowedWindowSizes.length === 0;

  const tsGuardrailConflict = React.useMemo((): string | null => {
    if (!isDatasetLoaded) return null;
    const unitGuardrail =
      tsGuardrails.units[currentConfig.timeUnit as TimeSeriesUnit];
    if (!unitGuardrail.enabled) {
      return `Time unit "${currentConfig.timeUnit}" has been disabled by the dataset's guardrail policy.`;
    }
    const isRollingEnabled = currentConfig.rollingAverage?.enabled ?? false;
    const windowSize = (currentConfig.rollingAverage?.windowSize ??
      0) as GuardrailRollingOption;
    const effectiveOption: GuardrailRollingOption = isRollingEnabled
      ? windowSize
      : 0;
    if (!unitGuardrail.allowedRollingAverages.includes(effectiveOption)) {
      return isRollingEnabled
        ? `Rolling average of ${windowSize} periods is no longer allowed for ${currentConfig.timeUnit} by dataset policy.`
        : `No Rolling Average is no longer allowed for ${currentConfig.timeUnit} by dataset policy.`;
    }
    return null;
  }, [
    isDatasetLoaded,
    tsGuardrails,
    currentConfig.timeUnit,
    currentConfig.rollingAverage,
  ]);

  // Resolved endpoint for "latest_available" mode from Available Through date
  const resolvedEndDate = React.useMemo(() => {
    if (!dataAvailabilityDate) return null;
    return resolveLatestAvailableEndDate(
      dataAvailabilityDate,
      currentConfig.timeUnit as "week" | "month" | "quarter" | "semester",
      currentConfig.responsesMinDate,
    );
  }, [
    dataAvailabilityDate,
    currentConfig.timeUnit,
    currentConfig.responsesMinDate,
  ]);

  const resolvedEndDateSummary = React.useMemo(() => {
    if (!dataAvailabilityDate) return null;
    return resolveLatestAvailableSummary(
      dataAvailabilityDate,
      currentConfig.timeUnit as "week" | "month" | "quarter" | "semester",
      currentConfig.responsesMinDate,
    );
  }, [
    dataAvailabilityDate,
    currentConfig.timeUnit,
    currentConfig.responsesMinDate,
  ]);

  const fallbackEndDateSummary = React.useMemo(() => {
    if (!isAvailabilityCleared || !currentConfig.responsesMaxDate) return null;
    return resolveLatestAvailableSummary(
      currentConfig.responsesMaxDate,
      currentConfig.timeUnit as "week" | "month" | "quarter" | "semester",
    );
  }, [
    isAvailabilityCleared,
    currentConfig.responsesMaxDate,
    currentConfig.timeUnit,
  ]);

  const isMultiSurveyDataset = React.useMemo(() => {
    const dataset = datasets.find((d) => d.id === datasetId);
    const surveyCount = dataset?.surveyIds?.length ?? 0;
    if (surveyCount <= 1) return false;

    const configs = weightConfigData?.weightConfigurations ?? [];
    if (configs.length !== surveyCount) return true;

    const first = configs[0];
    return !configs.every(
      (c) =>
        c.datasetElementQuestionId === first.datasetElementQuestionId &&
        c.variableLabel === first.variableLabel,
    );
  }, [datasets, datasetId, weightConfigData]);
  const [windowExceedsData, setWindowExceedsData] = useState(false);
  const [currentWindowSize, setCurrentWindowSize] = useState(2);

  // Update rolling average footer summary when rolling average config changes
  React.useEffect(() => {
    const { updateFooterSummary } = lineChartConfigDispatcher(dispatch);

    if (
      currentConfig.rollingAverage?.enabled &&
      currentConfig.rollingAverage.windowSize
    ) {
      const description = getRollingAverageDescriptionForLineChart(
        currentConfig.rollingAverage.windowSize,
        currentConfig.timeUnit,
      );
      const note = getRollingAverageNoteForLineChart(
        currentConfig.rollingAverage.windowSize,
      );

      updateFooterSummary({
        rollingAverageDescription: description,
        rollingAverageNote: note,
      });
    } else {
      // Clear rolling average footer info when rolling average is disabled
      updateFooterSummary({
        rollingAverageDescription: undefined,
        rollingAverageNote: undefined,
      });
    }
  }, [
    currentConfig.rollingAverage?.enabled,
    currentConfig.rollingAverage?.windowSize,
    currentConfig.timeUnit,
    dispatch,
  ]);

  // Use the line chart config drawer hook for data fetching
  const {
    isRefetchingData,
    fetchLineChartData,
    localApplyFilters,
    handleFilterToggle,
    handleStatTestingChange,
    handleRollingAverageChange: handleRollingAverageChangeFromHook,
    isSaving,
  } = useLineChartConfigDrawer();

  const metricSource = useLineMetricSourceRevalidation(
    datasetId,
    datasetDetails?.metrics ?? undefined,
    !!datasetDetails,
    fetchLineChartData,
  );
  const isMetricSourced = currentConfig.chartSourceType === "metric";

  const {
    base: visualizationBase,
    isValid: isVisualizationBaseValid,
    setBase: setVisualizationBase,
  } = useLineChartVisualizationBase();

  // Refetch immediately on any Base change — the Redux dispatch above is
  // async relative to this closure, so the updated base is computed locally
  // and passed as an override rather than read back from currentConfig.
  // Persist the new base and recalc the preview with it as an override (the
  // Redux dispatch is async relative to this closure).
  const handleVisualizationBaseChange = (updatedBase: VisualizationBase) => {
    setVisualizationBase(updatedBase);
    // Renaming a Custom Base is a cosmetic label change with no effect on the
    // resolved filters — skip the refetch so it doesn't fire on every keystroke.
    if (isOnlyBaseNameChanged(currentConfig.visualizationBase, updatedBase)) return;
    fetchLineChartData(
      localApplyFilters,
      undefined, // overrideStatTesting
      undefined, // overrideWeight
      undefined, // overrideTimeUnit
      undefined, // overrideDates
      undefined, // overridePeriodCount
      undefined, // overrideBreakout
      undefined, // overridePerLineWeights
      undefined, // overrideFocusParams
      undefined, // overrideRollup
      undefined, // overrideCategoryRollups
      updatedBase, // overrideVisualizationBase
    );
  };

  const [showBreakoutValidation, setShowBreakoutValidation] = useState(false);

  const isCreatingChart = editingWidgetId === "new-widget";

  // Compute header title with rolling average suffix when title auto-sync is active
  const headerTitle = React.useMemo(() => {
    if (!questions?.name) return "Configure your line chart";

    let baseTitle = questions.name;

    // Check if user has customized the title
    const hasCustomTitle =
      currentConfig.title !== "" &&
      questions &&
      currentConfig.title !== questions.name;

    // If title is customized, show the custom title
    if (hasCustomTitle) {
      return currentConfig.title;
    }

    // Add focus entity label if focus mode is enabled
    if (currentConfig.focusEntityDisplayLabel) {
      baseTitle = `${baseTitle}  (${currentConfig.focusEntityDisplayLabel})`;
    }

    // If title auto-sync is active, append rolling average suffix
    const rollingSuffix =
      currentConfig.rollingAverage?.enabled &&
      currentConfig.rollingAverage?.windowSize
        ? getRollingAverageTitleSuffix(currentConfig.rollingAverage.windowSize)
        : "";

    return `${baseTitle}${rollingSuffix}`;
  }, [
    questions,
    currentConfig.title,
    currentConfig.focusEntityDisplayLabel,
    currentConfig.rollingAverage,
    currentConfig.effectiveRollingPeriodCount,
  ]);

  // Calculate missing data warning when rolling window extends before dataset start
  const missingDataWarning = React.useMemo(() => {
    if (
      !currentConfig.rollingAverage?.enabled ||
      !currentConfig.rollingAverage.windowSize
    ) {
      return null;
    }
    return getMissingDataWarningForLineChart(
      currentConfig.rollingAverage.windowSize,
      currentConfig.timeUnit,
      currentConfig.startDate,
      currentConfig.responsesMinDate,
    );
  }, [
    currentConfig.rollingAverage,
    currentConfig.timeUnit,
    currentConfig.startDate,
    currentConfig.responsesMinDate,
  ]);

  // Check if current time period is "all_time" - statistical testing should be disabled
  // For line charts, all_time means both startDate and endDate are null
  const isAllTimePeriod =
    currentConfig.startDate === null && currentConfig.endDate === null;

  // Track if we've fetched for this drawer session to prevent double fetches
  const hasFetchedRef = React.useRef(false);
  const lastWidgetIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    console.log("🔍 Drawer Effect:", {
      isDrawerOpen,
      isCreatingChart,
      editingWidgetId,
      datasetId,
      questionId,
      hasFetched: hasFetchedRef.current,
    });

    if (!isDrawerOpen) {
      hasFetchedRef.current = false;
      lastWidgetIdRef.current = null;
      return;
    }

    const isNewSession = lastWidgetIdRef.current !== editingWidgetId;

    // Metric-sourced: wait for useLineMetricSourceRevalidation to apply the
    // live definition to Redux first, so this fires once against the
    // current focus/Base/population instead of once against the save-time
    // snapshot immediately followed by a second, corrected fetch (see
    // useMetricSourceRevalidation.ts's doc comment for the full race).
    if (
      isDrawerOpen &&
      !isCreatingChart &&
      datasetId &&
      questionId &&
      isDatasetLoaded &&
      metricSource.isSettled &&
      (!hasFetchedRef.current || isNewSession)
    ) {
      console.log("✅ Calling fetchLineChartData");
      hasFetchedRef.current = true;
      lastWidgetIdRef.current = editingWidgetId;

      const shouldApplyFiltersForInitialLoad =
        activeFilters.length > 0 && localApplyFilters;

      // If endDateMode is latest_available, always resolve the correct endDate at fetch time.
      // Cleared (Available Through removed): send null so API returns latest available data.
      // Normal: use resolvedEndDate derived from dataAvailabilityDate.
      const overrideDates =
        currentConfig.endDateMode === "latest_available"
          ? {
              startDate: currentConfig.startDate,
              endDate: isAvailabilityCleared ? null : resolvedEndDate,
            }
          : undefined;

      fetchLineChartData(
        shouldApplyFiltersForInitialLoad,
        undefined,
        undefined,
        undefined,
        overrideDates,
      );
    } else {
      console.log("❌ NOT calling fetchLineChartData - conditions not met");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, isCreatingChart, editingWidgetId, isDatasetLoaded, metricSource.isSettled]);

  const { closeChartDrawer } = chartDrawerDispatcher(dispatch);
  const {
    updateTitle,
    updateSubtitle,
    updateTitleBold,
    updateTitleUnderline,
    updateTimeUnit,
    updateStartDate,
    updateEndDate,
    updateEndDateMode,
    updateWeight,
    updatePerLineWeights,
    updateShowMarkers,
    updateColorPalette,
    updateShowLegend,
    updateFocusSelection,
    updateFocusParams,
    updateFocusRollup,
    addCategoryRollup,
    updateCategoryRollup,
    removeCategoryRollup,
    updateLineVisibility,
    toggleAllLines,
    updateStatTestingEnabled,
    updateStatTestType,
    updateSignificanceLevel,
    updateMinimumSampleSize,
    updateStatMarkerStyle,
    updateStatTestingTimeStrategy,
    saveConfig,
    resetToDefault,
    updateYAxisRangeMode,
    updateYAxisMin,
    updateYAxisMax,
    updatePercentageFormatting,
    updateCustomLineLabel,
    resetCustomLineLabel,
    reorderLines,
    updateSortingOption,
    updateRollingAverageEnabled,
    updateRollingAverageWindowSize,
    updateBreakoutEnabled,
    updateBreakoutSourceLine,
    updateBreakoutQuestion,
    updateBreakoutFocusMode,
    updateBreakoutFocusVariable,
    updateBreakoutFocusValue,
    setDataAvailabilityDate,
    updateCustomColor,
    setAllCustomColors,
  } = lineChartConfigDispatcher(dispatch);
  console.log(currentConfig.breakout);

  // ─── Focus selection editing (variable/value level, single category vs rollup) ───
  // Gated the same way as the create-widget modal: only Single questions with
  // multiple variables ever get focusMode/focusRollup set. Widgets saved before the
  // rollup feature existed only have focusMode/focusEntity (no focusRollup), so
  // localFocusMetric below naturally derives "single" for them — no migration needed.
  const hasFocusSelection = !!(
    currentConfig.focusMode || currentConfig.focusRollup
  );

  // Metric-sourced: questionId starts out as the chart's save-time source
  // question and gets corrected to the metric's live source question once
  // useLineMetricSourceRevalidation settles (see updateResolvedQuestionId).
  // Skip until then so this never fires once against the stale id and again
  // against the corrected one.
  const isQuestionIdUnresolved = isMetricSourced && !metricSource.isSettled;

  const { data: focusQuestionVariablesData } =
    useGetDatasetQuestionVariablesByElementQuery(
      { id: datasetId!, dataset_element_id: questionId! },
      {
        skip:
          !datasetId ||
          !questionId ||
          !hasFocusSelection ||
          isQuestionIdUnresolved,
      },
    );

  const allFocusVariables = React.useMemo(
    () => focusQuestionVariablesData?.variables || [],
    [focusQuestionVariablesData],
  );
  const allFocusValues = React.useMemo(
    () => focusQuestionVariablesData?.values || [],
    [focusQuestionVariablesData],
  );

  const [localFocusMetric, setLocalFocusMetric] = useState<
    "single" | "rollup"
  >("single");
  const rollupIdRef = React.useRef<string>(
    currentConfig.focusRollup?.id ?? crypto.randomUUID(),
  );
  const savedRollupRef = React.useRef<{
    rollup: WidgetRollup;
    name: string;
  } | null>(null);
  const savedSingleRef = React.useRef<{
    focusMode: string;
    focusEntity: string;
    focusEntityDisplayLabel?: string;
  } | null>(null);

  React.useEffect(() => {
    if (isDrawerOpen) {
      const isRollup = !!currentConfig.focusRollup;
      setLocalFocusMetric(isRollup ? "rollup" : "single");
      rollupIdRef.current = currentConfig.focusRollup?.id ?? crypto.randomUUID();
      savedRollupRef.current = null;
      savedSingleRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, editingWidgetId]);

  const focusLevel = React.useMemo((): "variable" | "value" => {
    const raw = currentConfig.focusMode?.toLowerCase();
    return raw === "value" ? "value" : "variable";
  }, [currentConfig.focusMode]);

  const selectedFocusVariableId = React.useMemo(() => {
    if (localFocusMetric === "rollup" || focusLevel !== "variable") return "";
    return (
      allFocusVariables.find(
        (v: any) => v.label === currentConfig.focusEntity,
      )?.datasetElementId ?? ""
    );
  }, [localFocusMetric, focusLevel, allFocusVariables, currentConfig.focusEntity]);

  const selectedFocusValueId = React.useMemo(() => {
    if (localFocusMetric === "rollup" || focusLevel !== "value") return "";
    return (
      allFocusValues.find(
        (v: any) => String(v.value) === currentConfig.focusEntity,
      )?.datasetElementId ?? ""
    );
  }, [localFocusMetric, focusLevel, allFocusValues, currentConfig.focusEntity]);

  const isFocusValid = React.useMemo(() => {
    // Metric-sourced charts never let the researcher edit focus/rollup — it's
    // read-only, sourced from the live metric via
    // useLineMetricSourceRevalidation's handleRefresh, which updates
    // currentConfig.focusMode/focusRollup directly without going through the
    // Question path's localFocusMetric toggle. Validating against that stale
    // local state here would incorrectly disable Save right after a Refresh
    // whenever the live definition's single/rollup shape differs from
    // whatever the chart had saved before (same bug fixed for Bar Chart's
    // BarChartConfigDrawer.tsx).
    if (isMetricSourced) return true;
    if (!currentConfig.focusMode && !currentConfig.focusRollup) return true;

    if (localFocusMetric === "rollup") {
      const trimmed = (currentConfig.focusRollupName ?? "").trim();
      if (
        !trimmed ||
        trimmed.length > 100 ||
        !ALLOWED_ROLLUP_NAME_CHARS.test(trimmed)
      )
        return false;
      const lower = trimmed.toLowerCase();
      const hasConflict = (items: any[]) =>
        items.some(
          (item: any) =>
            item.title?.trim().toLowerCase() === lower ||
            item.rowtitle?.trim().toLowerCase() === lower ||
            item.label?.trim().toLowerCase() === lower,
        );
      if (hasConflict(allFocusVariables) || hasConflict(allFocusValues))
        return false;
      if ((currentConfig.focusRollup?.sourceCategoryLabels?.length ?? 0) < 2)
        return false;
      return true;
    }
    return !!currentConfig.focusEntity;
  }, [
    isMetricSourced,
    localFocusMetric,
    currentConfig.focusMode,
    currentConfig.focusRollupName,
    currentConfig.focusRollup,
    currentConfig.focusEntity,
    allFocusVariables,
    allFocusValues,
  ]);

  const handleFocusLevelChange = (level: "variable" | "value") => {
    const upperLevel = level.toUpperCase();
    updateFocusParams({ focusMode: upperLevel, focusEntity: "" });
    if (localFocusMetric === "rollup") {
      handleRollupLabelsChange([]);
    }
  };

  const handleFocusMetricChange = (metric: "single" | "rollup") => {
    setLocalFocusMetric(metric);
    if (metric === "rollup") {
      if (currentConfig.focusEntity) {
        savedSingleRef.current = {
          focusMode: currentConfig.focusMode ?? "VARIABLE",
          focusEntity: currentConfig.focusEntity,
          focusEntityDisplayLabel: currentConfig.focusEntityDisplayLabel,
        };
      }
      if (savedRollupRef.current) {
        rollupIdRef.current = savedRollupRef.current.rollup.id;
        updateFocusRollup(savedRollupRef.current);
        if (
          (savedRollupRef.current.rollup.sourceCategoryLabels?.length ?? 0) >=
          2
        ) {
          fetchLineChartData(
            localApplyFilters,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            savedRollupRef.current.rollup,
          );
        }
        savedRollupRef.current = null;
      } else {
        rollupIdRef.current = crypto.randomUUID();
        updateFocusParams({
          focusMode: currentConfig.focusMode ?? "VARIABLE",
          focusEntity: "",
        });
        updateFocusRollup(null);
      }
    } else {
      if (currentConfig.focusRollup) {
        savedRollupRef.current = {
          rollup: currentConfig.focusRollup,
          name: currentConfig.focusRollupName ?? "",
        };
      }
      updateFocusRollup(null);
      if (savedSingleRef.current) {
        updateFocusParams(savedSingleRef.current);
        fetchLineChartData(
          localApplyFilters,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          {
            focusMode: savedSingleRef.current.focusMode,
            focusEntity: savedSingleRef.current.focusEntity,
          },
          // Explicit null (not undefined) — currentConfig.focusRollup in this
          // closure is still the stale pre-dispatch value until the next render,
          // so omitting this would fall through to the old rollup and re-send it.
          null,
        );
        savedSingleRef.current = null;
      } else {
        updateFocusParams({
          focusMode: currentConfig.focusMode ?? "VARIABLE",
          focusEntity: "",
        });
        updateFocusSelection({ mode: focusLevel });
      }
    }
  };

  const handleSingleFocusParamsChange = (
    params: {
      focusMode: string;
      focusEntity: string;
      focusEntityDisplayLabel?: string;
    } | null,
  ) => {
    if (!params) return;
    updateFocusParams(params);
    fetchLineChartData(
      localApplyFilters,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      { focusMode: params.focusMode, focusEntity: params.focusEntity },
    );
  };

  const handleRollupNameChange = (name: string) => {
    const labels = currentConfig.focusRollup?.sourceCategoryLabels ?? [];
    updateFocusRollup({
      rollup: {
        id: rollupIdRef.current,
        calculationType: "SUM_SELECTED",
        sourceCategoryLabels: labels,
      },
      name,
    });
  };

  const handleRollupLabelsChange = (labels: string[]) => {
    const newRollup: WidgetRollup = {
      id: rollupIdRef.current,
      calculationType: "SUM_SELECTED",
      sourceCategoryLabels: labels,
    };
    updateFocusRollup({
      rollup: newRollup,
      name: currentConfig.focusRollupName ?? "",
    });
    if (labels.length >= 2) {
      fetchLineChartData(
        localApplyFilters,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        newRollup,
      );
    }
  };

  // ─── Category (line) rollups ───────────────────────────────────────────────
  // Mean-score trend lines from Numeric/Float questions aren't eligible source lines.
  const isMeanScoreQuestion =
    currentConfig.questionType === "Float" ||
    currentConfig.questionType === "Number";

  const isBreakoutEnabled = currentConfig.breakout?.enabled ?? false;

  // Grid Line Charts (Single question, multiple variables) only expose an eligible
  // line dimension once Focus Selection or a Focus Rollup has resolved it.
  const isGridLineDimensionResolved =
    !hasFocusSelection ||
    !!currentConfig.focusEntity ||
    (currentConfig.focusRollup?.sourceCategoryLabels?.length ?? 0) >= 2;

  const activeCategoryRollups = React.useMemo(() => {
    const scope = getCategoryRollupScope(currentConfig.focusMode);
    return currentConfig.categoryRollupsByScope?.[scope] ?? [];
  }, [currentConfig.focusMode, currentConfig.categoryRollupsByScope]);

  const categoryRollupLineOptions = React.useMemo(() => {
    const rollupLineNames = new Set(
      activeCategoryRollups
        .map((r) => r.name)
        .filter((n): n is string => !!n),
    );

    // Non-rollup lines currently visible in the series.
    const options: Array<{
      sourceLabel: string;
      displayLabel: string;
      originalLabel?: string;
    }> = currentConfig.series
      .filter((s) => !rollupLineNames.has(s.name))
      .map((s) => {
        const displayLabel = currentConfig.customLineLabels[s.id] ?? s.name;
        return {
          sourceLabel: s.id,
          displayLabel,
          originalLabel: displayLabel !== s.name ? s.name : undefined,
        };
      });

    // Source lines absorbed by rollups are absent from currentConfig.series.
    // Re-add them using lineIdLabelMap so they appear as selectable/deselectable
    // options when editing a rollup.
    const presentLabels = new Set(options.map((o) => o.sourceLabel));
    for (const rollup of activeCategoryRollups) {
      for (const lineId of rollup.sourceCategoryLabels) {
        if (!presentLabels.has(lineId)) {
          const originalName = currentConfig.lineIdLabelMap?.[lineId];
          if (originalName) {
            const displayLabel =
              currentConfig.customLineLabels[lineId] ?? originalName;
            options.push({
              sourceLabel: lineId,
              displayLabel,
              originalLabel:
                displayLabel !== originalName ? originalName : undefined,
            });
            presentLabels.add(lineId);
          }
        }
      }
    }

    return options;
  }, [
    currentConfig.series,
    currentConfig.customLineLabels,
    currentConfig.lineIdLabelMap,
    activeCategoryRollups,
  ]);

  // Rollups whose source lines have dropped below 2 resolvable entries since they were
  // saved (e.g. a variable was removed from the dataset) — can no longer calculate correctly.
  const staleCategoryRollups = React.useMemo(() => {
    const knownLineIds = new Set(
      categoryRollupLineOptions.map((o) => o.sourceLabel),
    );
    return activeCategoryRollups.filter(
      (r) =>
        r.sourceCategoryLabels.filter((id) => knownLineIds.has(id)).length <
        2,
    );
  }, [activeCategoryRollups, categoryRollupLineOptions]);

  // Section is offered once there are enough unassigned lines to build a rollup, or
  // there are already-saved rollups to manage; Breakout keeps it visible but disabled.
  const showLineRollupsSection =
    !isMetricSourced &&
    !isMeanScoreQuestion &&
    isGridLineDimensionResolved &&
    (isBreakoutEnabled ||
      categoryRollupLineOptions.length >= 2 ||
      activeCategoryRollups.length > 0);

  const handleCategoryRollupCreate = (rollup: WidgetRollup) => {
    addCategoryRollup(rollup);
    const updatedRollups = [...activeCategoryRollups, rollup];
    fetchLineChartData(
      localApplyFilters,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      updatedRollups,
    );
  };

  const handleCategoryRollupUpdate = (rollup: WidgetRollup) => {
    updateCategoryRollup(rollup);
    const updatedRollups = activeCategoryRollups.map((r) =>
      r.id === rollup.id ? rollup : r,
    );
    fetchLineChartData(
      localApplyFilters,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      updatedRollups,
    );
  };

  const handleCategoryRollupDelete = (rollupId: string) => {
    removeCategoryRollup(rollupId);
    const updatedRollups = activeCategoryRollups.filter(
      (r) => r.id !== rollupId,
    );
    fetchLineChartData(
      localApplyFilters,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      undefined,
      updatedRollups,
    );
  };

  const circleMarkersActive =
    currentConfig.statisticalTesting?.enabled === true &&
    currentConfig.statisticalTesting?.markerStyle === "circles";

  // Default new charts to "latest_available" when dataAvailabilityDate is configured
  const hasDefaultedEndDateModeRef = React.useRef(false);
  React.useEffect(() => {
    if (
      isCreatingChart &&
      isDrawerOpen &&
      dataAvailabilityDate &&
      !hasDefaultedEndDateModeRef.current
    ) {
      hasDefaultedEndDateModeRef.current = true;
      updateEndDateMode("latest_available");
    }
    if (!isDrawerOpen) {
      hasDefaultedEndDateModeRef.current = false;
    }
  }, [isCreatingChart, isDrawerOpen, dataAvailabilityDate, updateEndDateMode]);

  // Clear original-color tracking when switching away from Custom palette
  React.useEffect(() => {
    if (currentConfig.colorPalette !== "Custom") {
      setOriginalColors({});
    }
  }, [currentConfig.colorPalette]);

  // When creating a new chart, default the weight to DATASET_DEFAULT if the
  // selected dataset has at least one weight configuration.
  React.useEffect(() => {
    if (
      editingWidgetId === "new-widget" &&
      (weightConfigData?.weightConfigurations?.length ?? 0) > 0 &&
      currentConfig.weight === "DISABLED"
    ) {
      updateWeight("DATASET_DEFAULT");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingWidgetId, weightConfigData]);

  // Keep dataAvailabilityDate in sync on the config so the widget can use it
  React.useEffect(() => {
    if (isDatasetLoaded) {
      setDataAvailabilityDate(dataAvailabilityDate ?? null);
    }
  }, [isDatasetLoaded, dataAvailabilityDate, setDataAvailabilityDate]);

  // Auto-disable Statistical Testing when switching to "all_time" period
  React.useEffect(() => {
    if (isAllTimePeriod && currentConfig.statisticalTesting?.enabled) {
      // Just disable the toggle without triggering refetch
      updateStatTestingEnabled(false);
    }
  }, [
    isAllTimePeriod,
    updateStatTestingEnabled,
    currentConfig.statisticalTesting?.enabled,
  ]);

  // Apply the dataset-default rolling average for new charts. The time unit is
  // already chosen in the create modal (dataset default, or the user's override)
  // and applied before the drawer opens — so we must NOT reset it here, only
  // derive the rolling-average default for the unit the user actually picked.
  const hasAppliedTsDefaultsRef = React.useRef(false);
  React.useEffect(() => {
    if (
      isCreatingChart &&
      isDrawerOpen &&
      isDatasetLoaded &&
      !hasAppliedTsDefaultsRef.current
    ) {
      hasAppliedTsDefaultsRef.current = true;
      const selectedUnit = currentConfig.timeUnit;
      const selectedUnitGuardrail =
        tsGuardrails.units[selectedUnit as TimeSeriesUnit];
      if (selectedUnitGuardrail.defaultRollingAverage === 0) {
        updateRollingAverageEnabled(false);
      } else {
        updateRollingAverageEnabled(true);
        updateRollingAverageWindowSize(
          selectedUnitGuardrail.defaultRollingAverage,
        );
      }
    }
    if (!isDrawerOpen) {
      hasAppliedTsDefaultsRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreatingChart, isDrawerOpen, isDatasetLoaded]);

  const handleSave = async () => {
    // Revert any unapplied color changes before saving
    Object.entries(originalColors).forEach(([lineId, color]) => {
      updateCustomColor({ lineId, color });
    });
    setOriginalColors({});

    // Block save when chart is in latest_available mode but Available Through is no longer set
    if (
      currentConfig.endDateMode === "latest_available" &&
      !dataAvailabilityDate
    ) {
      return;
    }

    // Validate breakout config if enabled
    const b = currentConfig.breakout;
    if (b?.enabled) {
      const isBreakoutValid = b.sourceLineId && b.questionId;
      if (!isBreakoutValid) {
        setShowBreakoutValidation(true);
        return;
      }
    }
    setShowBreakoutValidation(false);

    // Set the saving flag to prevent useEffects from triggering
    isSaving.current = true;

    // Store the current preview state
    const wasShowingFiltered = localApplyFilters;

    // Only fetch unfiltered data if filters were applied
    if (
      wasShowingFiltered &&
      activeFilters.length > 0 &&
      datasetId &&
      questionId
    ) {
      console.log("💾 Line Chart: Saving - fetching unfiltered data first");
      await fetchLineChartData(false);
    }

    if (pendingMemoryEntry) {
      questionSessionMemory.push(pendingMemoryEntry.dashboardId, {
        questionId: pendingMemoryEntry.questionId,
        questionType: pendingMemoryEntry.questionType,
        datasetId: pendingMemoryEntry.datasetId,
        slot: "general",
      });
    }

    // Strip stale viewer RA values — if guardrails removed an option that was
    // previously saved in chartUnitAdditionalRollingAverages, it would still show
    // up in the client popover even though the researcher can't see or uncheck it.
    const chartUnitGuardrailRAs =
      tsGuardrails.units[currentConfig.timeUnit as TimeSeriesUnit]
        ?.allowedRollingAverages ?? [];
    const sanitizedAdditionalRAs = (
      currentConfig.viewerOptions?.chartUnitAdditionalRollingAverages ?? []
    ).filter((ra) =>
      chartUnitGuardrailRAs.includes(ra),
    ) as typeof currentConfig.viewerOptions.chartUnitAdditionalRollingAverages;
    if (
      sanitizedAdditionalRAs.length !==
      (currentConfig.viewerOptions?.chartUnitAdditionalRollingAverages
        ?.length ?? 0)
    ) {
      lineChartConfigDispatcher(dispatch).updateViewerOptions({
        ...currentConfig.viewerOptions,
        chartUnitAdditionalRollingAverages: sanitizedAdditionalRAs,
      });
    }

    // Save the config with unfiltered data
    saveConfig(editingWidgetId || "new-widget");

    // Close drawer after 50ms to allow saveConfig to propagate to ChartWidget,
    // which triggers onWidgetSaved → filter re-application via DashboardFilters
    setTimeout(() => {
      closeChartDrawer();
      isSaving.current = false;
    }, 50);
  };

  const handleDiscard = () => {
    resetToDefault();
    closeChartDrawer();
  };

  const handleTimeUnitChange = async (
    newTimeUnit: "quarter" | "semester" | "month" | "week",
  ) => {
    hasAppliedTsDefaultsRef.current = true;
    updateTimeUnit(newTimeUnit);

    // Reset rolling average to dataset default for the newly selected unit
    const newUnitGuardrail = tsGuardrails.units[newTimeUnit as TimeSeriesUnit];
    const defaultRolling = newUnitGuardrail.defaultRollingAverage;
    const shouldEnable = defaultRolling !== 0;
    updateRollingAverageEnabled(shouldEnable);
    let newWindowSize: number | undefined = undefined;
    if (shouldEnable) {
      newWindowSize = defaultRolling;
      updateRollingAverageWindowSize(defaultRolling);
    }

    // When in latest_available mode, resolve the new endpoint for the new time unit.
    // Use Available Through if set, otherwise fall back to responsesMaxDate.
    let newEndDate: string | null | undefined = undefined;
    if (currentConfig.endDateMode === "latest_available") {
      const boundary =
        dataAvailabilityDate || currentConfig.responsesMaxDate || null;
      if (boundary) {
        const resolved = resolveLatestAvailableEndDate(
          boundary,
          newTimeUnit as "week" | "month" | "quarter" | "semester",
        );
        newEndDate = resolved ?? null;
        updateEndDate(newEndDate);
      }
    }

    // Fetch data immediately with new time unit AND new window size
    if (datasetId && questionId) {
      await fetchLineChartData(
        localApplyFilters,
        undefined,
        undefined,
        newTimeUnit,
        currentConfig.endDateMode === "latest_available"
          ? { startDate: currentConfig.startDate, endDate: newEndDate ?? null }
          : undefined,
        newWindowSize,
      );
    }
  };

  const handleWeightChange = async (
    newWeight: "DISABLED" | "DATASET_DEFAULT" | "PER_LINE_WEIGHT",
  ) => {
    updateWeight(newWeight);
    // Fetch data immediately with new weight - pass it directly to avoid Redux state delay
    // Only refetch if not switching to per-line (per-line needs weights configured first)
    if (datasetId && questionId && newWeight !== "PER_LINE_WEIGHT") {
      await fetchLineChartData(localApplyFilters, undefined, newWeight);
    }
  };

  const handleApplyFiltersToggle = (checked: boolean) => {
    handleFilterToggle(checked);
  };

  const handlePreviewDateFilterToggle = async (enabled: boolean) => {
    setUsePreviewDateFilter(enabled);

    // Refetch line chart data with dashboard date filters when enabled
    if (datasetId && questionId) {
      let overrideDates:
        | { startDate?: string | null; endDate?: string | null }
        | undefined;

      if (enabled) {
        const params = new URLSearchParams(window.location.search);
        const dfStart = params.get("df_start");
        const dfEnd = params.get("df_end");

        if (dfStart && dfEnd) {
          // Trim to complete periods matching the current time unit, same as
          // the dashboard viewer does in DashboardFilters (Module 2 strict completeness).
          const timeUnit = (currentConfig.timeUnit?.toLowerCase() ??
            "quarter") as "week" | "month" | "quarter" | "semester";
          const trimmed = trimDateRangeToCompletePeriods(
            dfStart,
            dfEnd,
            timeUnit,
          );
          overrideDates = trimmed
            ? { startDate: trimmed.startDate, endDate: trimmed.endDate }
            : { startDate: null, endDate: null };
        }
      }

      await fetchLineChartData(
        localApplyFilters,
        undefined, // stat testing
        undefined, // weight
        undefined, // time unit
        overrideDates,
      );
    }
  };

  // Keep endDate in sync with the effective endpoint when in latest_available mode.
  // Normal: use resolvedEndDate from Available Through.
  // Cleared (Available Through removed): use null so API returns latest available data.
  React.useEffect(() => {
    if (currentConfig.endDateMode !== "latest_available") return;
    const effectiveEnd = isAvailabilityCleared ? null : resolvedEndDate;
    if (effectiveEnd !== undefined && currentConfig.endDate !== effectiveEnd) {
      updateEndDate(effectiveEnd);
    }
  }, [
    currentConfig.endDateMode,
    isAvailabilityCleared,
    resolvedEndDate,
    currentConfig.endDate,
    updateEndDate,
  ]);

  const handleEndDateModeChange = async (
    mode: "fixed" | "latest_available",
  ) => {
    updateEndDateMode(mode);
    if (mode === "latest_available" && resolvedEndDate) {
      await handleDateRangeChange(currentConfig.startDate, resolvedEndDate);
    } else if (mode === "fixed") {
      // Reset end date to latest date with data in the dataset
      const resetEndDate = currentConfig.responsesMaxDate ?? null;
      if (resetEndDate) {
        await handleDateRangeChange(currentConfig.startDate, resetEndDate);
      }
    }
  };

  const handleDateRangeChange = async (
    startDate: string | null,
    endDate: string | null,
  ) => {
    // Clear previous error
    setDateError("");

    // Validate dates — in latest_available mode the end is a resolved period boundary,
    // not user-chosen, so allow start > resolved end (empty state handles that case).
    if (currentConfig.endDateMode !== "latest_available") {
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (end < start) {
          setDateError("The End Date cannot be earlier than the Start Date.");
          return;
        }
      } else if (startDate || endDate) {
        setDateError(
          "Please select both start and end dates for the custom period.",
        );
      }
    }

    // Update Redux state
    if (startDate !== null) {
      updateStartDate(startDate);
    }
    if (endDate !== null) {
      updateEndDate(endDate);
    }

    // Fetch data immediately with new date range (only if both dates are set and valid)
    // Skip fetch when start is after resolved end in latest_available mode — empty state handles it.
    const isStartAfterResolvedEnd =
      currentConfig.endDateMode === "latest_available" &&
      startDate &&
      endDate &&
      new Date(startDate) > new Date(endDate);
    if (
      startDate &&
      endDate &&
      !dateError &&
      !isStartAfterResolvedEnd &&
      datasetId &&
      questionId
    ) {
      await fetchLineChartData(
        localApplyFilters,
        undefined,
        undefined,
        undefined,
        { startDate, endDate },
      );
    }
  };

  const handleRollingAverageChange = async (windowSize: number | null) => {
    // Use the hook handler for refetching with rolling average settings
    await handleRollingAverageChangeFromHook(windowSize);
  };

  // Handler for per-line weight changes with automatic refetch
  const handlePerLineWeightsChange = async (
    weights: Record<
      string,
      {
        surveyId: string;
        configurationId: string;
        datasetElementQuestionId: string;
        datasetElementVariableId: string;
        weightVariableLabel: string;
      }
    >,
  ) => {
    console.log("🟢 LineChartConfigDrawer - handlePerLineWeightsChange:", {
      weights,
      hasWeights: Object.keys(weights).length > 0,
      datasetId,
      questionId,
      currentWeight: currentConfig.weight,
    });

    // Update Redux state first
    updatePerLineWeights(weights);

    // Always refetch when weight selection is PER_LINE_WEIGHT
    // - If weights object has entries: apply per-line weights
    // - If weights object is empty: reset to default weights
    // This ensures "Set All To Default Weight" button triggers a refetch
    if (datasetId && questionId && currentConfig.weight === "PER_LINE_WEIGHT") {
      console.log("✅ Calling fetchLineChartData with weights:", weights);
      await fetchLineChartData(
        localApplyFilters,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        weights, // Pass weights directly (can be empty to reset to default)
      );
    } else {
      console.log("❌ NOT calling fetchLineChartData:", {
        weightMode: currentConfig.weight,
        datasetId,
        questionId,
      });
    }
  };

  // Check if we have valid data for the chart
  const isAvailableData =
    !currentConfig.series || currentConfig.series.length === 0;

  const isViewerOptionsInvalid =
    currentConfig.viewerOptions?.enabled &&
    Object.keys(currentConfig.viewerOptions.additionalUnits).length === 0 &&
    currentConfig.viewerOptions.chartUnitAdditionalRollingAverages.length === 0;

  // Latest-available empty-state conditions (PRD Module 2)
  const latestAvailableEndBeforeStart =
    currentConfig.endDateMode === "latest_available" &&
    resolvedEndDate !== null &&
    currentConfig.startDate !== null &&
    resolvedEndDate <= currentConfig.startDate;

  // No complete period before the availability cutoff that also overlaps with actual data
  // (resolvedEndDate is null when the last complete period is before responsesMinDate).
  const latestAvailableNoCompletePeriod =
    currentConfig.endDateMode === "latest_available" &&
    dataAvailabilityDate != null &&
    resolvedEndDate === null;

  const hasNoPreviousPeriodData =
    !isRefetchingData &&
    currentConfig.statisticalTesting?.enabled &&
    currentConfig.series.length > 0 &&
    currentConfig.series.every((series) =>
      series.data.every(
        (point) =>
          point.statSignificance === undefined ||
          point.statSignificance === null ||
          point.statSignificance === 0,
      ),
    );

  // Check if there are any significant differences found
  const hasSignificantDifferences =
    currentConfig.statisticalTesting?.enabled &&
    currentConfig.series.length > 0 &&
    currentConfig.series.some((series) =>
      series.data.some(
        (point) =>
          point.statSignificance === 1 || point.statSignificance === -1,
      ),
    );

  const hasNoSignificantDifferences =
    !isRefetchingData &&
    currentConfig.statisticalTesting?.enabled &&
    currentConfig.series.length > 0 &&
    !hasNoPreviousPeriodData &&
    !hasSignificantDifferences;

  // Check if sample size is below threshold
  const hasSampleSizeBelowThreshold =
    !isRefetchingData &&
    currentConfig.statisticalTesting?.enabled &&
    currentConfig.series.length > 0 &&
    currentConfig.series.some((series) =>
      series.data.some(
        (point) =>
          point.baseN !== undefined &&
          point.baseN <
            (currentConfig.statisticalTesting?.minimumSampleSize || 30),
      ),
    );

  // Check if rolling average has partial historical data (less than requested window)
  // This happens when: rollingAverage is enabled AND effectiveRollingPeriodCount is less than windowSize
  const hasPartialHistoricalData =
    currentConfig.rollingAverage?.enabled &&
    currentConfig.statisticalTesting?.enabled &&
    currentConfig.effectiveRollingPeriodCount !== undefined &&
    currentConfig.rollingAverage?.windowSize !== undefined &&
    currentConfig.effectiveRollingPeriodCount > 0 &&
    currentConfig.effectiveRollingPeriodCount <
      currentConfig.rollingAverage.windowSize;

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={() => closeChartDrawer()}
      sx={{
        "& .MuiDrawer-paper": {
          width: "80vw",
          boxSizing: "border-box",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        {/* Header */}
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div className="flex-1">
            <Typography className="font-bold" variant="h6">
              {questions?.label || "Line Chart Configuration"}
            </Typography>
            <Typography variant="body1">{headerTitle}</Typography>
            {datasetName && (
              <Typography variant="caption" color="text.secondary">
                Dataset: {datasetName}
              </Typography>
            )}
          </div>
          {(() => {
            const hasDashboardDateFilters = !!(
              new URLSearchParams(window.location.search).get("df_start") &&
              new URLSearchParams(window.location.search).get("df_end")
            );
            return (
              <>
                <DrawerFilterToggles
                  hasDashboardDateFilters={hasDashboardDateFilters}
                  usePreviewDateFilter={usePreviewDateFilter}
                  onPreviewDateFilterToggle={handlePreviewDateFilterToggle}
                  activeFiltersCount={activeFilters.length}
                  localApplyFilters={localApplyFilters}
                  onFilterToggle={() =>
                    handleApplyFiltersToggle(!localApplyFilters)
                  }
                  disabled={isRefetchingData}
                />
              </>
            );
          })()}
        </Box>

        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Configuration Panel */}
          <Box
            sx={{
              width: 400,
              borderRight: 1,
              borderColor: "divider",
              overflow: "auto",
              p: 3,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {isMetricSourced && (
                <Box>
                  <MetricSourceSummarySection
                    datasetId={datasetId}
                    datasetMetricName={currentConfig.datasetMetricName ?? null}
                    liveMetric={metricSource.liveMetric}
                    questionDetail={metricSource.questionDetail}
                    isRemoved={metricSource.isRemoved}
                    isUnavailableMean={metricSource.isUnavailableMean}
                    definitionChanged={metricSource.definitionChanged}
                    breakoutSourceInvalidated={metricSource.breakoutSourceInvalidated}
                    isResolving={metricSource.isResolving}
                    fetchError={metricSource.fetchError}
                    onRefresh={metricSource.handleRefresh}
                    onRetryFetch={metricSource.retryFetch}
                  />
                  <Divider sx={{ mt: 3 }} />
                </Box>
              )}

              {/* Time Configuration Section */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
                  Time Configuration
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {/* Time Unit Selection */}
                  <FormControl fullWidth size="small">
                    <FormLabel className="text-[12px] text-[#262626]">
                      Time Unit
                    </FormLabel>
                    <Select
                      value={currentConfig.timeUnit}
                      onChange={(e) =>
                        handleTimeUnitChange(
                          e.target.value as
                            | "quarter"
                            | "semester"
                            | "month"
                            | "week",
                        )
                      }
                      disabled={isRefetchingData}
                    >
                      {tsGuardrails.units.quarter.enabled && (
                        <MenuItem value="quarter">Quarter</MenuItem>
                      )}
                      {tsGuardrails.units.semester.enabled && (
                        <MenuItem value="semester">Semester</MenuItem>
                      )}
                      {tsGuardrails.units.month.enabled && (
                        <MenuItem value="month">Month</MenuItem>
                      )}
                      {tsGuardrails.units.week.enabled && (
                        <MenuItem value="week">Week</MenuItem>
                      )}
                    </Select>
                  </FormControl>

                  {/* Date Range Selection */}
                  <FormControl fullWidth size="small">
                    <FormLabel className="text-[12px] text-[#262626]">
                      Start Date
                    </FormLabel>
                    <DateInput
                      value={currentConfig.startDate || ""}
                      onChange={(e) => {
                        const effectiveEndDate =
                          currentConfig.endDateMode === "latest_available"
                            ? isAvailabilityCleared
                              ? null
                              : resolvedEndDate
                            : currentConfig.endDate;
                        handleDateRangeChange(e, effectiveEndDate);
                      }}
                      disabled={isRefetchingData}
                    />
                    {currentConfig.responsesMinDate && (
                      <Typography variant="caption" color="text.secondary">
                        Available range:{" "}
                        {(() => {
                          // Format date as MM/DD/YYYY without timezone conversion
                          const formatDate = (dateStr: string) => {
                            if (!dateStr) return "";
                            const [year, month, day] = dateStr.split("-");
                            return `${month.padStart(2, "0")}/${day.padStart(2, "0")}/${year}`;
                          };
                          return `${formatDate(currentConfig.responsesMinDate)} to ${formatDate(currentConfig.responsesMaxDate || "")}`;
                        })()}
                      </Typography>
                    )}
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <FormLabel className="text-[12px] text-[#262626]">
                      End Date
                    </FormLabel>

                    {/* Show End Date Mode selector when Available Through is configured,
                        OR when chart is already in latest_available mode (so user can switch back to fixed) */}
                    {dataAvailabilityDate ||
                    currentConfig.endDateMode === "latest_available" ? (
                      <>
                        <RadioGroup
                          row
                          value={currentConfig.endDateMode}
                          onChange={(e) =>
                            handleEndDateModeChange(
                              e.target.value as "fixed" | "latest_available",
                            )
                          }
                        >
                          <FormControlLabel
                            value="fixed"
                            control={<Radio size="small" />}
                            label="Fixed Date"
                            disabled={isRefetchingData}
                          />
                          <FormControlLabel
                            value="latest_available"
                            control={<Radio size="small" />}
                            label="Latest Available Date"
                            disabled={isRefetchingData || !dataAvailabilityDate}
                          />
                        </RadioGroup>

                        {currentConfig.endDateMode === "latest_available" ? (
                          !isDatasetLoaded ? null : isAvailabilityCleared ? (
                            <>
                              {fallbackEndDateSummary ? (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    mt: 0.5,
                                    display: "block",
                                    fontStyle: "italic",
                                  }}
                                >
                                  {fallbackEndDateSummary}
                                </Typography>
                              ) : null}
                              <Alert
                                severity="warning"
                                sx={{ mt: 1, fontSize: "0.75rem" }}
                              >
                                Available Through has been cleared. Previewing
                                with the latest date with data as a temporary
                                boundary. Switch to Fixed Date or restore
                                Available Through in the dataset to save.
                              </Alert>
                            </>
                          ) : resolvedEndDateSummary ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{
                                mt: 0.5,
                                display: "block",
                                fontStyle: "italic",
                              }}
                            >
                              {resolvedEndDateSummary}
                            </Typography>
                          ) : (
                            <Alert
                              severity="warning"
                              sx={{ mt: 1, fontSize: "0.75rem" }}
                            >
                              No complete {currentConfig.timeUnit} period exists
                              within the current availability window.
                            </Alert>
                          )
                        ) : (
                          <DateInput
                            value={currentConfig.endDate || ""}
                            onChange={(e) => {
                              handleDateRangeChange(currentConfig.startDate, e);
                            }}
                            disabled={isRefetchingData}
                          />
                        )}
                      </>
                    ) : (
                      <>
                        <DateInput
                          value={currentConfig.endDate || ""}
                          onChange={(e) => {
                            handleDateRangeChange(currentConfig.startDate, e);
                          }}
                          disabled={isRefetchingData}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mt: 0.5 }}
                        >
                          Set the Dataset Available Through to enable Latest
                          Available Data.
                        </Typography>
                      </>
                    )}
                  </FormControl>

                  {/* Date validation error message */}
                  {dateError && (
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {dateError}
                    </Alert>
                  )}

                  {/* Weight Selection */}
                  <FormControl fullWidth size="small">
                    <FormLabel className="text-[12px] text-[#262626]">
                      Weight Selection
                    </FormLabel>
                    <Select
                      value={currentConfig.weight}
                      onChange={(e) =>
                        handleWeightChange(
                          e.target.value as
                            | "DISABLED"
                            | "DATASET_DEFAULT"
                            | "PER_LINE_WEIGHT",
                        )
                      }
                      disabled={isRefetchingData}
                    >
                      <MenuItem value="DISABLED">Disabled</MenuItem>
                      <MenuItem value="DATASET_DEFAULT">
                        Use Default Weight
                      </MenuItem>
                      <MenuItem value="PER_LINE_WEIGHT">
                        Per-Line Weighting
                      </MenuItem>
                    </Select>
                  </FormControl>

                  {currentConfig.weight === "DISABLED" && (
                    <Alert
                      severity="warning"
                      icon={<WarningIcon fontSize="small" />}
                      sx={{
                        fontSize: "0.75rem",
                        "& .MuiAlert-message": { fontSize: "0.75rem" },
                      }}
                    >
                      Weights disabled: unweighted counts shown.
                    </Alert>
                  )}

                  {currentConfig.weight === "DATASET_DEFAULT" &&
                    !weightsApplied && (
                      <Alert
                        severity="warning"
                        icon={<WarningIcon fontSize="small" />}
                        sx={{
                          fontSize: "0.75rem",
                          "& .MuiAlert-message": { fontSize: "0.75rem" },
                        }}
                      >
                        No default weight configured for this dataset. Showing
                        unweighted counts.
                      </Alert>
                    )}

                  {/* Per-Line Weight Configuration */}
                  {currentConfig.weight === "PER_LINE_WEIGHT" && datasetId && (
                    <PerLineWeightConfig
                      datasetId={datasetId}
                      lineLabels={currentConfig.series.map((s) => s.name)}
                      lineIds={currentConfig.series.map((s) => s.id)}
                      customLineLabels={currentConfig.customLineLabels || {}}
                      hiddenLines={Object.entries(currentConfig.lineVisibility)
                        .filter(([, visible]) => !visible)
                        .map(([lineId]) => lineId)}
                      onWeightsChange={handlePerLineWeightsChange}
                      disabled={isRefetchingData}
                    />
                  )}
                </Box>
              </Box>

              <Divider className="mx-[-24px]" />
              {/* Breakout Section — for a metric chart, Source Line comes from
                  the metric's represented results instead of question focus/type
                  (PRD-67). */}
              <LineChartBreakoutSection
                breakout={currentConfig.breakout}
                datasetId={datasetId}
                mainQuestionId={isQuestionIdUnresolved ? null : questionId}
                mainQuestionType={currentConfig.questionType ?? questions?.type}
                mainFocusMode={currentConfig.focusSelection?.mode}
                isMetricSourced={isMetricSourced}
                metricRepresentedResults={metricSource.representedResults}
                disabledReason={
                  activeCategoryRollups.length > 0
                    ? isMetricSourced
                      ? "Breakout isn't available for a metric using rolled-up categories."
                      : "Delete existing line rollups before enabling Breakout."
                    : undefined
                }
                onToggle={(enabled) => {
                  // Mutually exclusive with line rollups — the switch itself is disabled
                  // above, this is a defensive guard against stale-closure toggles.
                  if (enabled && activeCategoryRollups.length > 0) return;
                  updateBreakoutEnabled(enabled);
                  // When toggling off, re-fetch without breakout params to restore original chart
                  if (!enabled && datasetId && questionId) {
                    fetchLineChartData(
                      localApplyFilters,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      { enabled: false, sourceLineId: null, questionId: null },
                    );
                  }
                  // When toggling on, do NOT fetch yet — wait for source line + question selection
                }}
                onBackfillSourceLineLabel={(id, label) => {
                  updateBreakoutSourceLine({ id, label });
                }}
                onSourceLineChange={(value) => {
                  updateBreakoutSourceLine(value);
                  // Only fetch when all 3 are set: breakout on + source line + question
                  const questionId_ = currentConfig.breakout.questionId;
                  if (datasetId && questionId && value?.id && questionId_) {
                    fetchLineChartData(
                      localApplyFilters,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      {
                        enabled: true,
                        sourceLineId: value.id,
                        questionId: questionId_,
                      },
                    );
                  }
                }}
                onQuestionChange={(q) => {
                  // Convert DatasetQuestionsSearchResponse to BreakoutQuestionOption for Redux
                  const breakoutQ = q
                    ? {
                        id: q.datasetElementId,
                        label: q.label,
                        title: q.title,
                        type: (q.type === "multi" ? "Multiple" : "Single") as
                          | "Single"
                          | "Multiple",
                        variableCount: q.variablesCount,
                        valueCount: q.valuesCount,
                      }
                    : null;
                  updateBreakoutQuestion(breakoutQ);
                  // Only fetch when all 3 are set: breakout on + source line + question
                  const sourceLineId_ = currentConfig.breakout.sourceLineId;
                  if (
                    datasetId &&
                    questionId &&
                    sourceLineId_ &&
                    q?.datasetElementId
                  ) {
                    fetchLineChartData(
                      localApplyFilters,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      {
                        enabled: true,
                        sourceLineId: sourceLineId_,
                        questionId: q.datasetElementId,
                      },
                    );
                  }
                }}
                onFocusModeChange={(mode) => {
                  updateBreakoutFocusMode(mode);
                  // Reset entity — don't fetch yet, wait for variable/value selection
                }}
                onFocusVariableChange={(id, entityLabel) => {
                  updateBreakoutFocusVariable({ id, entityLabel });
                  const sourceLineId_ = currentConfig.breakout.sourceLineId;
                  const questionId_ = currentConfig.breakout.questionId;
                  if (
                    datasetId &&
                    questionId &&
                    sourceLineId_ &&
                    questionId_ &&
                    id &&
                    entityLabel
                  ) {
                    fetchLineChartData(
                      localApplyFilters,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      {
                        enabled: true,
                        sourceLineId: sourceLineId_,
                        questionId: questionId_,
                        focusMode: "VARIABLE",
                        crossFocusEntity: entityLabel,
                      },
                    );
                  }
                }}
                onFocusValueChange={(id, entityValue) => {
                  updateBreakoutFocusValue({ id, entityValue });
                  const sourceLineId_ = currentConfig.breakout.sourceLineId;
                  const questionId_ = currentConfig.breakout.questionId;
                  if (
                    datasetId &&
                    questionId &&
                    sourceLineId_ &&
                    questionId_ &&
                    id &&
                    entityValue
                  ) {
                    fetchLineChartData(
                      localApplyFilters,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      undefined,
                      {
                        enabled: true,
                        sourceLineId: sourceLineId_,
                        questionId: questionId_,
                        focusMode: "VALUE",
                        crossFocusEntity: entityValue,
                      },
                    );
                  }
                }}
                showValidation={showBreakoutValidation}
                disabled={isRefetchingData || activeCategoryRollups.length > 0}
              />

              <Divider className="mx-[-24px]" />
              {/* Rolling Average Section */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
                  Rolling Average
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <LineChartRollingAverageSection
                    enabled={currentConfig.rollingAverage?.enabled || false}
                    windowSize={currentConfig.rollingAverage?.windowSize || 2}
                    timeUnit={currentConfig.timeUnit}
                    totalAvailablePeriods={
                      currentConfig.breakout?.enabled &&
                      currentConfig.breakout?.questionId
                        ? countPeriodsInRange(
                            currentConfig.startDate,
                            currentConfig.endDate,
                            currentConfig.timeUnit,
                          )
                        : currentConfig.categories?.length || 0
                    }
                    onEnabledChange={(enabled) => {
                      updateRollingAverageEnabled(enabled);
                      handleRollingAverageChange(
                        enabled
                          ? currentConfig.rollingAverage?.windowSize || 2
                          : null,
                      );
                    }}
                    onWindowSizeChange={(size) => {
                      updateRollingAverageWindowSize(size);
                      handleRollingAverageChange(size);
                    }}
                    warningMessage={currentConfig.rollingPeriodsWarningMessage}
                    onWindowExceedsDataChange={(exceedsData, windowSize) => {
                      setWindowExceedsData(exceedsData);
                      setCurrentWindowSize(windowSize);
                    }}
                    effectiveRollingPeriodCount={
                      currentConfig.effectiveRollingPeriodCount
                    }
                    disabled={isRefetchingData}
                    guardrailAllowedWindowSizes={guardrailAllowedWindowSizes}
                    guardrailNoRollingDisabled={guardrailNoRollingDisabled}
                    guardrailRollingDisabled={guardrailRollingDisabled}
                    guardrailConflictWarning={tsGuardrailConflict ?? undefined}
                  />
                </Box>
              </Box>

              <Divider className="mx-[-24px]" />

              {!isMetricSourced && (
                <BaseSection
                  datasetId={datasetId}
                  isDisabled={isRefetchingData}
                  base={visualizationBase}
                  onChange={handleVisualizationBaseChange}
                />
              )}

              <Divider className="mx-[-24px]" />

              {/* Viewer Time Options Section */}
              <TimeSeriesViewerOptionsSection
                viewerOptions={currentConfig.viewerOptions}
                chartTimeUnit={currentConfig.timeUnit}
                chartRollingWindowSize={
                  currentConfig.rollingAverage?.enabled
                    ? (currentConfig.rollingAverage?.windowSize ?? 0)
                    : 0
                }
                tsGuardrailUnits={tsGuardrails.units}
                isDisabled={isRefetchingData}
                onViewerOptionsChange={(opts) =>
                  lineChartConfigDispatcher(dispatch).updateViewerOptions(opts)
                }
                datasetId={datasetId}
                onRestoreSharedTime={(config) => {
                  updateTimeUnit(
                    config.savedDefault as typeof currentConfig.timeUnit,
                  );
                  const ra = config.savedRollingAverage;
                  updateRollingAverageEnabled(ra > 0);
                  if (ra > 0) updateRollingAverageWindowSize(ra);
                  if (config.viewerOptions) {
                    lineChartConfigDispatcher(dispatch).updateViewerOptions(
                      config.viewerOptions as typeof currentConfig.viewerOptions,
                    );
                  }
                }}
              />

              <Divider className="mx-[-24px]" />

              {/* Focus Mode Section (for Single questions with multiple variables) */}
              {!isMetricSourced && hasFocusSelection && (
                <FocusModeSection
                  focusLevel={focusLevel}
                  allVariables={allFocusVariables}
                  allValues={allFocusValues}
                  isDisabled={isRefetchingData}
                  focusMetric={localFocusMetric}
                  selectedVariableId={selectedFocusVariableId}
                  selectedValueId={selectedFocusValueId}
                  rollupName={currentConfig.focusRollupName ?? ""}
                  rollupSelectedLabels={
                    currentConfig.focusRollup?.sourceCategoryLabels ?? []
                  }
                  onFocusLevelChange={handleFocusLevelChange}
                  onFocusParamsChange={handleSingleFocusParamsChange}
                  onFocusMetricChange={handleFocusMetricChange}
                  onRollupNameChange={handleRollupNameChange}
                  onRollupLabelsChange={handleRollupLabelsChange}
                  displayUnit="lines"
                />
              )}

              {/* Styling & Customization Section */}
              <Accordion
                defaultExpanded
                sx={{ boxShadow: "none", border: "1px solid #eee" }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Styling & Customization
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                  >
                    <Box sx={{ position: "relative" }}>
                      <HPInput
                        label="Chart title"
                        value={currentConfig.title}
                        onChange={(e) => updateTitle(e.target.value)}
                        disabled={isRefetchingData}
                      />
                      <Box
                        sx={{
                          position: "absolute",
                          bottom: "70%",
                          right: 0,
                          display: "flex",
                          gap: 0.5,
                        }}
                      >
                        <ToggleButton
                          value="bold"
                          selected={currentConfig.titleBold}
                          onChange={() =>
                            updateTitleBold(!currentConfig.titleBold)
                          }
                          size="small"
                          disabled={isRefetchingData}
                          sx={{
                            width: 32,
                            height: 32,
                            minWidth: 32,
                            border: "1px solid #e0e0e0",
                            "&.Mui-selected": {
                              backgroundColor: "#1976d2",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#1565c0",
                              },
                            },
                          }}
                        >
                          <FormatBoldIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton
                          value="underline"
                          selected={currentConfig.titleUnderline}
                          onChange={() =>
                            updateTitleUnderline(!currentConfig.titleUnderline)
                          }
                          size="small"
                          disabled={isRefetchingData}
                          sx={{
                            width: 32,
                            height: 32,
                            minWidth: 32,
                            border: "1px solid #e0e0e0",
                            "&.Mui-selected": {
                              backgroundColor: "#1976d2",
                              color: "white",
                              "&:hover": {
                                backgroundColor: "#1565c0",
                              },
                            },
                          }}
                        >
                          <FormatUnderlinedIcon fontSize="small" />
                        </ToggleButton>
                      </Box>
                    </Box>

                    <HPInput
                      label="Subtitle"
                      value={currentConfig.subtitle}
                      onChange={(e) => updateSubtitle(e.target.value)}
                      placeholder="Optional"
                      disabled={isRefetchingData}
                    />

                    {/* Marker Dots Toggle */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography>Marker Dots</Typography>
                      <IOSSwitch
                        checked={currentConfig.showMarkers}
                        onChange={(e) => updateShowMarkers(e.target.checked)}
                        size="small"
                        disabled={isRefetchingData || circleMarkersActive}
                      />
                    </Box>

                    {/* Color Palette */}
                    <FormControl fullWidth size="small">
                      <FormLabel className="text-[12px] text-[#262626]">
                        Color Palette
                      </FormLabel>
                      <Select
                        value={currentConfig.colorPalette}
                        onChange={(e) => updateColorPalette(e.target.value)}
                        disabled={isRefetchingData}
                        renderValue={(value) => (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                              justifyContent: "space-between",
                              flexDirection: "row-reverse",
                            }}
                          >
                            <Box
                              sx={{
                                width: 24,
                                height: 12,
                                borderRadius: "16px",
                                background:
                                  {
                                    Orange: "#ffb09c",
                                    Green: "#57dfbe",
                                    Black: "#757679",
                                    Purple: "#a1a7df",
                                    Crimson: "#c36179",
                                    "Dark Green": "#57a68c",
                                    Blue: "#57bfe7",
                                    Yellow: "#ebdd7c",
                                    Multicolor:
                                      "linear-gradient(90deg, #ffb09c 0%, #57dfbe 25%, #757679 50%, #a1a7df 75%, #c36179 100%)",
                                    Custom:
                                      "linear-gradient(90deg, #ffb09c 0%, #57dfbe 25%, #757679 50%, #a1a7df 75%, #c36179 100%)",
                                  }[value as string] || "#57bfe7",
                              }}
                            />
                            {value === "Custom" ? "Custom (per-line)" : value}
                          </Box>
                        )}
                      >
                        <MenuItem value="Orange">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                backgroundColor: "#ffb09c",
                              }}
                            />
                            Orange
                          </Box>
                        </MenuItem>
                        <MenuItem value="Green">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                backgroundColor: "#57dfbe",
                              }}
                            />
                            Green
                          </Box>
                        </MenuItem>
                        <MenuItem value="Blue">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                backgroundColor: "#57bfe7",
                              }}
                            />
                            Blue
                          </Box>
                        </MenuItem>
                        <MenuItem value="Yellow">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                backgroundColor: "#ebdd7c",
                              }}
                            />
                            Yellow
                          </Box>
                        </MenuItem>
                        <MenuItem value="Multicolor">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(90deg, #ffb09c 0%, #57dfbe 25%, #757679 50%, #a1a7df 75%, #c36179 100%)",
                              }}
                            />
                            Multicolor
                          </Box>
                        </MenuItem>
                        <MenuItem value="Custom">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                width: 16,
                                height: 16,
                                borderRadius: "50%",
                                background:
                                  "linear-gradient(90deg, #ffb09c 0%, #57dfbe 25%, #757679 50%, #a1a7df 75%, #c36179 100%)",
                                border: "1.5px solid #999",
                              }}
                            />
                            Custom (per-line)
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>

                    {/* Per-line color pickers (Custom palette only) */}
                    {currentConfig.colorPalette === "Custom" &&
                      currentConfig.series.length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                          }}
                        >
                          <ApplyColorToAll
                            defaultColor={
                              currentConfig.customColors?.[
                                currentConfig.series[0].id
                              ] || CHART_COLORS[0]
                            }
                            appliedColors={currentConfig.series
                              .filter(
                                (s) =>
                                  currentConfig.lineVisibility[s.id] !== false,
                              )
                              .map(
                                (s, index) =>
                                  currentConfig.customColors?.[s.id] ||
                                  CHART_COLORS[index % CHART_COLORS.length],
                              )}
                            onApply={(color) =>
                              setAllCustomColors({
                                lineIds: currentConfig.series
                                  .filter(
                                    (s) =>
                                      currentConfig.lineVisibility[s.id] !==
                                      false,
                                  )
                                  .map((s) => s.id),
                                color,
                              })
                            }
                          />
                          {currentConfig.series
                            .filter(
                              (s) =>
                                currentConfig.lineVisibility[s.id] !== false,
                            )
                            .map((series, index) => {
                              const label =
                                currentConfig.customLineLabels?.[series.id] ||
                                series.name;
                              const fallbackColor =
                                CHART_COLORS[index % CHART_COLORS.length];
                              const currentColor =
                                currentConfig.customColors?.[series.id] ||
                                fallbackColor;
                              const isDirty =
                                originalColors[series.id] !== undefined;
                              return (
                                <Box
                                  key={series.id}
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    gap: 1,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      flex: 1,
                                      overflow: "hidden",
                                      textOverflow: "ellipsis",
                                      whiteSpace: "nowrap",
                                      fontSize: "12px",
                                    }}
                                  >
                                    {label}
                                  </Typography>
                                  <input
                                    type="color"
                                    value={currentColor}
                                    onChange={(e) => {
                                      // Store original before first change so Cancel can revert
                                      if (!originalColors[series.id]) {
                                        setOriginalColors((prev) => ({
                                          ...prev,
                                          [series.id]: currentColor,
                                        }));
                                      }
                                      // Update Redux immediately for real-time chart preview
                                      updateCustomColor({
                                        lineId: series.id,
                                        color: e.target.value,
                                      });
                                    }}
                                    style={{
                                      width: 32,
                                      height: 24,
                                      cursor: "pointer",
                                      border: isDirty
                                        ? "2px solid #1976d2"
                                        : "1px solid #ccc",
                                      borderRadius: 4,
                                      padding: 0,
                                    }}
                                  />
                                  {isDirty && (
                                    <>
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        title="Apply color"
                                        onClick={() =>
                                          setOriginalColors((prev) => {
                                            const next = { ...prev };
                                            delete next[series.id];
                                            return next;
                                          })
                                        }
                                      >
                                        <CheckIcon fontSize="small" />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        title="Cancel"
                                        onClick={() => {
                                          // Revert Redux to the original color
                                          updateCustomColor({
                                            lineId: series.id,
                                            color: originalColors[series.id],
                                          });
                                          setOriginalColors((prev) => {
                                            const next = { ...prev };
                                            delete next[series.id];
                                            return next;
                                          });
                                        }}
                                      >
                                        <CloseIcon fontSize="small" />
                                      </IconButton>
                                    </>
                                  )}
                                </Box>
                              );
                            })}
                        </Box>
                      )}

                    {/* Legend Toggle */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Typography>Legend</Typography>
                      <IOSSwitch
                        checked={currentConfig.showLegend}
                        onChange={(e) => updateShowLegend(e.target.checked)}
                        size="small"
                        disabled={isRefetchingData}
                      />
                    </Box>

                    {/* Rounding */}
                    <FormControl fullWidth size="small">
                      <FormLabel className="text-[12px] text-[#262626]">
                        Rounding
                      </FormLabel>
                      <Select
                        value={currentConfig.percentageFormatting ?? 0}
                        onChange={(e) =>
                          updatePercentageFormatting(e.target.value as number)
                        }
                        disabled={isRefetchingData}
                      >
                        <MenuItem value={0}>0 decimals</MenuItem>
                        <MenuItem value={1}>1 decimal</MenuItem>
                        <MenuItem value={2}>2 decimals</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </AccordionDetails>
              </Accordion>

              <Divider className="mx-[-24px]" />

              {/* Y-Axis Range Section */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
                  Y-Axis Range
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <FormControl fullWidth size="small">
                    <FormLabel className="text-[12px] text-[#262626]">
                      Range Mode
                    </FormLabel>
                    <Select
                      value={currentConfig.yAxisRangeMode || "auto"}
                      onChange={(e) => {
                        const mode = e.target.value as YAxisRangeMode;
                        updateYAxisRangeMode(mode);
                        // Clear error when switching modes
                        setYAxisRangeError("");
                      }}
                      disabled={isRefetchingData}
                    >
                      <MenuItem value="auto">Auto</MenuItem>
                      <MenuItem value="custom">Custom</MenuItem>
                    </Select>
                  </FormControl>

                  {currentConfig.yAxisRangeMode === "custom" && (
                    <>
                      <Box sx={{ display: "flex", gap: 2 }}>
                        <TextField
                          label="Min Value"
                          type="number"
                          size="small"
                          fullWidth
                          value={currentConfig.yAxisMin ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const numValue =
                              value === "" ? undefined : parseFloat(value);

                            // Only update if it's a valid number or empty
                            if (value === "" || !isNaN(numValue!)) {
                              updateYAxisMin(numValue);

                              // Validate Min < Max
                              if (
                                numValue !== undefined &&
                                currentConfig.yAxisMax !== undefined
                              ) {
                                if (numValue >= currentConfig.yAxisMax) {
                                  setYAxisRangeError(
                                    "Min value must be less than Max value",
                                  );
                                } else {
                                  setYAxisRangeError("");
                                }
                              } else {
                                setYAxisRangeError("");
                              }
                            }
                          }}
                          disabled={isRefetchingData}
                          inputProps={{
                            step: "0.01",
                          }}
                          placeholder="e.g., 0, 10.5, -5.25"
                        />
                        <TextField
                          label="Max Value"
                          type="number"
                          size="small"
                          fullWidth
                          value={currentConfig.yAxisMax ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            const numValue =
                              value === "" ? undefined : parseFloat(value);

                            // Only update if it's a valid number or empty
                            if (value === "" || !isNaN(numValue!)) {
                              updateYAxisMax(numValue);

                              // Validate Min < Max
                              if (
                                numValue !== undefined &&
                                currentConfig.yAxisMin !== undefined
                              ) {
                                if (currentConfig.yAxisMin >= numValue) {
                                  setYAxisRangeError(
                                    "Min value must be less than Max value",
                                  );
                                } else {
                                  setYAxisRangeError("");
                                }
                              } else {
                                setYAxisRangeError("");
                              }
                            }
                          }}
                          disabled={isRefetchingData}
                          inputProps={{
                            step: "0.01",
                          }}
                          placeholder="e.g., 100, 95.5, 150.75"
                        />
                      </Box>

                      {yAxisRangeError && (
                        <Alert severity="error" sx={{ mt: -1 }}>
                          {yAxisRangeError}
                        </Alert>
                      )}

                      <FormHelperText>
                        Leave empty for no limit. Values outside data range will
                        be handled by Highcharts.
                      </FormHelperText>
                    </>
                  )}
                </Box>
              </Box>

              {/* Statistical Testing */}
              <Box>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700 }}>
                  Statistical Testing
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={
                          currentConfig.statisticalTesting?.enabled || false
                        }
                        onChange={(e) =>
                          handleStatTestingChange(
                            () => updateStatTestingEnabled(e.target.checked),
                            { enabled: e.target.checked },
                          )
                        }
                        size="small"
                        disabled={isRefetchingData || isAllTimePeriod}
                      />
                    }
                    label="Enable Statistical Testing"
                  />

                  {currentConfig.statisticalTesting?.enabled && (
                    <>
                      <FormControl fullWidth size="small">
                        <FormLabel className="text-[12px] text-[#262626]">
                          Test Type
                        </FormLabel>
                        <Select
                          value={
                            currentConfig.statisticalTesting?.testType ||
                            "z-test"
                          }
                          onChange={(e) =>
                            handleStatTestingChange(
                              () =>
                                updateStatTestType(
                                  e.target.value as StatTestType,
                                ),
                              { testType: e.target.value as StatTestType },
                            )
                          }
                          disabled={isRefetchingData}
                        >
                          <MenuItem value="z-test">
                            Z-Test (Proportions)
                          </MenuItem>
                          <MenuItem value="t-test">
                            T-Test (with Overlap)
                          </MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth size="small">
                        <FormLabel className="text-[12px] text-[#262626]">
                          Comparison Mode
                        </FormLabel>
                        <Select
                          value={
                            currentConfig.statisticalTesting?.timeStrategy ??
                            "PREVIOUS_PERIOD"
                          }
                          onChange={(e) =>
                            handleStatTestingChange(
                              () =>
                                updateStatTestingTimeStrategy(
                                  e.target
                                    .value as StatTestingTimeStrategy,
                                ),
                              {
                                timeStrategy: e.target
                                  .value as StatTestingTimeStrategy,
                              },
                            )
                          }
                          disabled={isRefetchingData}
                        >
                          <MenuItem value="PREVIOUS_PERIOD">
                            Period-over-Period
                          </MenuItem>
                          <MenuItem value="YEAR_OVER_YEAR">
                            Year-over-Year
                          </MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth size="small">
                        <FormLabel className="text-[12px] text-[#262626]">
                          Significance Level
                        </FormLabel>
                        <Select
                          value={
                            currentConfig.statisticalTesting
                              ?.significanceLevel || "90%"
                          }
                          onChange={(e) =>
                            handleStatTestingChange(
                              () =>
                                updateSignificanceLevel(
                                  e.target.value as SignificanceLevel,
                                ),
                              {
                                significanceLevel: e.target
                                  .value as SignificanceLevel,
                              },
                            )
                          }
                          disabled={isRefetchingData}
                        >
                          <MenuItem value="90%">90% Confidence</MenuItem>
                          <MenuItem value="95%">95% Confidence</MenuItem>
                          <MenuItem value="99%">99% Confidence</MenuItem>
                        </Select>
                      </FormControl>

                      <FormControl fullWidth size="small">
                        <FormLabel className="text-[12px] text-[#262626] mb-1">
                          Minimum Sample Size
                        </FormLabel>
                        <TextField
                          disabled={isRefetchingData}
                          type="text"
                          value={String(
                            currentConfig.statisticalTesting
                              ?.minimumSampleSize || 30,
                          )}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Only allow numbers (no letters, signs, or special characters)
                            if (value === "" || /^\d+$/.test(value)) {
                              const newSize = value === "" ? 0 : Number(value);
                              handleStatTestingChange(
                                () => updateMinimumSampleSize(newSize),
                                { minimumSampleSize: newSize },
                              );
                            }
                          }}
                          size="small"
                          fullWidth
                          inputProps={{
                            inputMode: "numeric",
                            pattern: "[0-9]*",
                          }}
                          sx={{
                            "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                              {
                                display: "none",
                              },
                            "& input[type=number]": {
                              MozAppearance: "textfield",
                            },
                          }}
                        />
                        <FormHelperText>
                          Minimum sample size required for statistical testing
                        </FormHelperText>
                      </FormControl>

                      <FormControl fullWidth size="small">
                        <FormLabel className="text-[12px] text-[#262626]">
                          Marker Style
                        </FormLabel>
                        <Select
                          value={
                            currentConfig.statisticalTesting?.markerStyle ??
                            "arrows"
                          }
                          onChange={(e) =>
                            handleStatTestingChange(
                              () =>
                                updateStatMarkerStyle(
                                  e.target.value as StatMarkerStyle,
                                ),
                              {
                                markerStyle: e.target.value as StatMarkerStyle,
                              },
                            )
                          }
                          disabled={isRefetchingData}
                        >
                          <MenuItem value="arrows">Arrows (▲ / ▼)</MenuItem>
                          <MenuItem value="circles">Circles (● / ○)</MenuItem>
                        </Select>
                      </FormControl>

                      <Box
                        sx={{
                          p: 2,
                          backgroundColor: "#f5f5f5",
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                        }}
                      >
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 600, display: "block", mb: 1 }}
                        >
                          Statistical Markers Legend:
                        </Typography>
                        {(() => {
                          const isYoY =
                            (currentConfig.statisticalTesting?.timeStrategy ??
                              "PREVIOUS_PERIOD") === "YEAR_OVER_YEAR";
                          const comparisonText = isYoY
                            ? "same period in prior year"
                            : currentConfig.rollingAverage?.enabled
                              ? "previous rolling window"
                              : "previous period";
                          const isArrows =
                            (currentConfig.statisticalTesting?.markerStyle ??
                              "arrows") === "arrows";
                          const upSymbol = isArrows ? "▲" : "●";
                          const downSymbol = isArrows ? "▼" : "○";
                          return (
                            <>
                              <Typography
                                variant="caption"
                                sx={{ display: "block" }}
                              >
                                <strong>{upSymbol}</strong> Significantly
                                higher than {comparisonText}
                              </Typography>
                              <Typography
                                variant="caption"
                                sx={{ display: "block" }}
                              >
                                <strong>{downSymbol}</strong> Significantly
                                lower than {comparisonText}
                              </Typography>
                            </>
                          );
                        })()}
                      </Box>
                    </>
                  )}

                  <Typography variant="caption" color="text.secondary">
                    Statistical testing helps identify significant differences
                    in your data over time
                  </Typography>
                </Box>
              </Box>

              <Divider className="mx-[-24px]" />

              {showLineRollupsSection && (
                <>
                  <CategoryRollupsSection
                    title="Line Rollups"
                    categoryRollups={activeCategoryRollups}
                    barOptions={categoryRollupLineOptions}
                    onCreateRollup={handleCategoryRollupCreate}
                    onUpdateRollup={handleCategoryRollupUpdate}
                    onDeleteRollup={handleCategoryRollupDelete}
                    itemLabel="lines"
                    nameLabel="Line Rollup Name*"
                    disabled={isBreakoutEnabled}
                    disabledMessage="Line rollups are unavailable while Breakout is enabled."
                    minSelectionMessage="At least two unassigned lines are required to create another line rollup."
                    overlapCapable={currentConfig.questionType === "Multiple"}
                  />

                  {!isBreakoutEnabled && (
                    <CategoryRollupsNotice
                      categoryRollups={activeCategoryRollups}
                      barOptions={categoryRollupLineOptions}
                      variableIdLabelMap={currentConfig.lineIdLabelMap}
                      labelOverrides={currentConfig.customLineLabels}
                      itemLabel="lines"
                      rollupLabel="line rollup"
                    />
                  )}

                  {staleCategoryRollups.length > 0 && (
                    <Alert severity="warning" sx={{ fontSize: "0.875rem" }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      >
                        Line rollups need attention
                      </Typography>
                      <Typography variant="body2">
                        The following line rollups now have fewer than two
                        available source lines and can no longer be
                        calculated reliably. Edit or delete them:{" "}
                        {staleCategoryRollups
                          .map((r) => r.name ?? r.id)
                          .join(", ")}
                        .
                      </Typography>
                    </Alert>
                  )}
                </>
              )}

              {/* Line Customization Panel */}
              {currentConfig.series.length > 0 && (
                <LineChartCustomizationPanel
                  lines={currentConfig.series.map((s) => ({
                    id: s.id,
                    name: s.name,
                  }))}
                  hiddenLines={currentConfig.lineVisibility}
                  customLineLabels={currentConfig.customLineLabels || {}}
                  lineOrder={
                    currentConfig.lineOrder ||
                    currentConfig.series.map((s) => s.id)
                  }
                  onToggleLineVisibility={(lineId, visible) =>
                    updateLineVisibility({ lineId, visible })
                  }
                  onUpdateLineLabel={(lineId, label) =>
                    updateCustomLineLabel({ lineId, label })
                  }
                  onResetLineLabel={(lineId) => resetCustomLineLabel(lineId)}
                  onShowAllLines={() => toggleAllLines(true)}
                  onHideAllLines={() => toggleAllLines(false)}
                  onReorderLines={(fromIndex, toIndex) =>
                    reorderLines({ fromIndex, toIndex })
                  }
                  sortingOption={currentConfig.sortingOption}
                  onUpdateSortingOption={updateSortingOption}
                  colorPalette={currentConfig.colorPalette}
                  customColors={currentConfig.customColors}
                />
              )}
            </Box>
          </Box>

          {/* Preview Panel */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              p: 3,
              overflow: "auto",
            }}
          >
            <Typography variant="h6" sx={{ mb: 2 }}>
              Preview
            </Typography>
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                height: 500,
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              {isRefetchingData || isQuestionIdUnresolved ? (
                <LoadingSpinner message="Loading data..." />
              ) : latestAvailableEndBeforeStart ? (
                <EmptyChartState message="No displayable data. The current availability window does not include any periods after the configured start date." />
              ) : latestAvailableNoCompletePeriod ? (
                <EmptyChartState message="No data available for the selected time unit within the current availability window." />
              ) : isAvailableData ? (
                <EmptyChartState message="This question has no response data available for visualization" />
              ) : (
                <ConfigurableLineChart />
              )}
            </Box>
            <Box
              sx={{
                mt: 2,
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              {hasNoPreviousPeriodData && (
                <Alert severity="warning">
                  No data available for the previous time period to perform
                  statistical testing.
                </Alert>
              )}

              {hasSampleSizeBelowThreshold && (
                <Alert severity="warning">
                  {currentConfig.rollingAverage?.enabled
                    ? "Warning: Weighted base for one or more rolling windows is below the recommended threshold for reliable statistical testing."
                    : "Warning: Sample size for one or more time periods is below the recommended threshold for reliable statistical testing."}
                </Alert>
              )}
              {/* Footer Summary */}
              <Box
                sx={{
                  p: 1,
                  backgroundColor: "#f9fafb",
                  borderRadius: 1,
                  borderTop: "1px solid #e0e0e0",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.25,
                      flex: 1,
                    }}
                  >
                    {currentConfig.visualizationBase?.enabled &&
                      currentConfig.visualizationBase.name && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={500}
                        >
                          Base: {currentConfig.visualizationBase.name}
                        </Typography>
                      )}
                    {(() => {
                      // Compute the filtered preview period when the dashboard
                      // date filter is active, mirroring the dashboard layout
                      // LineChart footer ("Period (filtered): start – end").
                      let previewFilterNote: string | null = null;
                      if (usePreviewDateFilter) {
                        const params = new URLSearchParams(
                          window.location.search,
                        );
                        const dfStart = params.get("df_start");
                        const dfEnd = params.get("df_end");
                        if (dfStart && dfEnd) {
                          const timeUnit =
                            (currentConfig.timeUnit?.toLowerCase() ??
                              "quarter") as
                              | "week"
                              | "month"
                              | "quarter"
                              | "semester";
                          const trimmed = trimDateRangeToCompletePeriods(
                            dfStart,
                            dfEnd,
                            timeUnit,
                          );
                          if (trimmed) {
                            previewFilterNote = `Period (filtered): ${trimmed.startDate} \u2013 ${trimmed.endDate}`;
                          } else {
                            const unitLabel =
                              timeUnit === "week"
                                ? "week"
                                : timeUnit === "month"
                                  ? "month"
                                  : timeUnit === "quarter"
                                    ? "quarter"
                                    : "semester";
                            previewFilterNote = `No complete ${unitLabel} available within the selected date range.`;
                          }
                        }
                      }
                      return previewFilterNote ? (
                        <Typography
                          variant="caption"
                          color="primary"
                          fontWeight={500}
                        >
                          Periodic: {currentConfig.footerSummary.periodic}
                          &nbsp;|&nbsp; {previewFilterNote}
                        </Typography>
                      ) : (
                        <>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={500}
                          >
                            Periodic: {currentConfig.footerSummary.periodic}
                            {currentConfig.footerSummary.period && (
                              <>
                                &nbsp;|&nbsp; Period:{" "}
                                {currentConfig.footerSummary.period}
                              </>
                            )}
                          </Typography>
                        </>
                      );
                    })()}
                    {currentConfig.rollingAverage?.enabled && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={500}
                      >
                        {(() => {
                          const periodLabels: Record<
                            typeof currentConfig.timeUnit,
                            string
                          > = {
                            quarter: "quarter",
                            semester: "semester",
                            month: "month",
                            week: "week",
                          };
                          const periodLabel =
                            periodLabels[currentConfig.timeUnit];
                          const windowSize =
                            currentConfig.rollingAverage.windowSize || 2;
                          const plural = windowSize > 1 ? "s" : "";
                          return `Rolling Average: ${windowSize} ${periodLabel}${plural}`;
                        })()}
                      </Typography>
                    )}
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 0.25,
                      alignItems: "flex-end",
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      Weight: {currentConfig.footerSummary.weight}
                    </Typography>
                    {currentConfig.breakout?.enabled &&
                      currentConfig.breakout.questionLabel &&
                      currentConfig.breakout.sourceLineId && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={500}
                        >
                          Breakout: {currentConfig.breakout.questionLabel} on{" "}
                          {currentConfig.breakout.sourceLineLabel ??
                            currentConfig.breakout.sourceLineId}
                        </Typography>
                      )}
                  </Box>
                </Box>
                {currentConfig.rollingAverage?.enabled && (
                  <>
                    <Alert severity="info">
                      Note: The first{" "}
                      {(currentConfig.rollingAverage?.windowSize ?? 2) - 1} data
                      points will show partial rolling averages with fewer than{" "}
                      {currentConfig.rollingAverage?.windowSize ?? 2} periods of
                      data.
                    </Alert>
                    {missingDataWarning && (
                      <Typography
                        variant="caption"
                        color="warning.main"
                        fontWeight={500}
                      >
                        {missingDataWarning}
                      </Typography>
                    )}
                    {windowExceedsData && (
                      <Alert severity="warning">
                        Warning: Selected rolling window ({currentWindowSize}{" "}
                        periods) exceeds available data. All data points will
                        show partial rolling averages.
                      </Alert>
                    )}
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            borderTop: 1,
            borderColor: "divider",
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
          }}
        >
          <HPButton variant="text" onClick={handleDiscard}>
            Discard
          </HPButton>
          <Tooltip
            title={
              tsGuardrailConflict
                ? tsGuardrailConflict
                : isViewerOptionsInvalid
                  ? "Viewer controls are enabled but no additional options are configured. Add at least one extra time unit or rolling average option."
                  : currentConfig.endDateMode === "latest_available" &&
                      !dataAvailabilityDate
                    ? "Available Through has been cleared from the dataset. Switch to Fixed Date to save."
                    : isAvailableData
                      ? "No data available"
                      : ""
            }
            arrow
            placement="top"
          >
            <span>
              <HPButton
                variant="contained"
                onClick={handleSave}
                disabled={
                  isAvailableData ||
                  isRefetchingData ||
                  !!tsGuardrailConflict ||
                  isViewerOptionsInvalid ||
                  !isFocusValid ||
                  !isVisualizationBaseValid ||
                  (currentConfig.weight === "PER_LINE_WEIGHT" &&
                    isMultiSurveyDataset) ||
                  (currentConfig.endDateMode === "latest_available" &&
                    !dataAvailabilityDate)
                }
              >
                {isCreatingChart ? "Save" : "Update"}
              </HPButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    </Drawer>
  );
};

export default LineChartConfigDrawer;
